import { observer } from 'mobx-react';
import React, { useState } from 'react';
import styled from 'styled-components';

import Overlay from 'shared/Overlay';
import Checkbox from 'shared/Checkbox';
import InputGroupComponent from 'components/groups/InputGroup';
import { VARIANT } from 'constants/inputTypes';
import useStores from 'utils/useStores';
import { addTestingIdentifier } from 'utils/testing';
import type { SplitMethod } from 'types/model/fraction';

import Fraction from '../models/Fraction';
import { MAX_PARTS, previewSplit } from '../utils/splitPreview';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1.5rem 1rem;
`;

const Options = styled.div`
  display: flex;
  gap: 2rem;
`;

export interface SplitFractionOverlayProps {
  fraction: Fraction;
  onClose: () => void;
  onSplit: () => void;
}

const describeWeights = (weights: number[]): string => {
  const groups = weights.reduce((acc, weight) => {
    acc.set(weight, (acc.get(weight) || 0) + 1);
    return acc;
  }, new Map<number, number>());

  return Array.from(groups.entries())
    .map(([weight, count]) => `${count} × ${weight} kg`)
    .join(' + ');
};

const SplitFractionOverlay = ({
  fraction,
  onClose,
  onSplit,
}: SplitFractionOverlayProps) => {
  const { fractionsStore } = useStores();
  const { bagWeightKg, bagWeightSource } = fractionsStore;

  const [method, setMethod] = useState<SplitMethod>(bagWeightKg ? 'by_bags' : 'by_count');
  const [bagsPerGroup, setBagsPerGroup] = useState(1);
  const [parts, setParts] = useState(2);
  const [errors, setErrors] = useState<string[]>([]);

  const amount = method === 'by_bags' ? bagsPerGroup : parts;
  const { weights, equalPartsFallback, partialBagKg } = previewSplit(
    fraction.splittableWeight,
    method,
    amount,
    bagWeightKg
  );
  const tooManyParts = weights.length > MAX_PARTS;
  const canConfirm = weights.length > 0 && !tooManyParts && !fraction.requestPending;

  const handleConfirm = async () => {
    const errorMessages = await fraction.split({
      split_method: method,
      bags_per_group: method === 'by_bags' ? bagsPerGroup : undefined,
      parts: method === 'by_count' ? parts : undefined,
    });

    if (errorMessages.length === 0) {
      onSplit();
    } else {
      setErrors(errorMessages);
    }
  };

  const renderPreview = () => {
    if (tooManyParts) {
      return (
        <p className='text-red'>
          {I18n.translate('fractions.split_overlay.too_many_parts', { max: MAX_PARTS })}
        </p>
      );
    }

    if (weights.length === 0) {
      return null;
    }

    return (
      <>
        <p {...addTestingIdentifier('split_preview')}>
          {I18n.translate('fractions.split_overlay.preview', {
            count: weights.length,
            weight: weights[0],
            weights: describeWeights(weights),
          })}
        </p>
        {/* PLA-860 v3 (D2): reparto "por cantidad" en sacos enteros o, si no alcanzan, iguales. */}
        {partialBagKg !== undefined && (
          <p className='text-gray'>
            {I18n.translate('fractions.split_overlay.whole_bags', {
              weight: partialBagKg,
            })}
          </p>
        )}
        {equalPartsFallback && (
          <p
            className='text-gray'
            {...addTestingIdentifier('split_equal_parts_fallback')}
          >
            {I18n.translate('fractions.split_overlay.equal_parts_fallback')}
          </p>
        )}
      </>
    );
  };

  return (
    <Overlay
      title={I18n.translate('fractions.split_overlay.title', {
        identifier: fraction.displayIdentifier,
      })}
      autoWidth={true}
      primaryText={I18n.translate('fractions.split_overlay.confirm')}
      onPrimaryClick={handleConfirm}
      primaryDisabled={!canConfirm}
      secondaryText={I18n.translate('fractions.split_overlay.cancel')}
      onSecondaryClick={onClose}
    >
      <Body {...addTestingIdentifier('split_overlay')}>
        <p>
          {I18n.translate('fractions.split_overlay.splittable_weight', {
            weight: fraction.splittableWeight,
          })}
        </p>

        <Options>
          <Checkbox
            asRadioButton={true}
            round={true}
            icon='tick'
            inline={true}
            name={`split_method_${fraction.id}`}
            id={`split_method_by_bags_${fraction.id}`}
            value='by_bags'
            dataCy='split_by_bags'
            checked={method === 'by_bags'}
            disabled={!bagWeightKg}
            onChange={() => setMethod('by_bags')}
            label={I18n.translate('fractions.split_overlay.by_bags')}
          />
          <Checkbox
            asRadioButton={true}
            round={true}
            icon='tick'
            inline={true}
            name={`split_method_${fraction.id}`}
            id={`split_method_by_count_${fraction.id}`}
            value='by_count'
            dataCy='split_by_count'
            checked={method === 'by_count'}
            onChange={() => setMethod('by_count')}
            label={I18n.translate('fractions.split_overlay.by_count')}
          />
        </Options>

        {method === 'by_bags' ? (
          <>
            <p>
              {I18n.translate('fractions.split_overlay.bag_weight', {
                weight: bagWeightKg,
                source: I18n.translate(
                  `fractions.split_overlay.bag_weight_source.${bagWeightSource}`
                ),
              })}
            </p>
            <InputGroupComponent
              label={I18n.translate('fractions.split_overlay.bags_per_group')}
              name='bags_per_group'
              variant={VARIANT.INTEGER}
              min={1}
              step={1}
              value={bagsPerGroup}
              onChange={(event) => setBagsPerGroup(parseInt(event.target.value, 10) || 0)}
              data-cy='split_bags_per_group'
            />
          </>
        ) : (
          <InputGroupComponent
            label={I18n.translate('fractions.split_overlay.parts')}
            name='parts'
            variant={VARIANT.INTEGER}
            min={2}
            step={1}
            value={parts}
            onChange={(event) => setParts(parseInt(event.target.value, 10) || 0)}
            data-cy='split_parts'
          />
        )}

        {!bagWeightKg && (
          <p className='text-gray'>
            {I18n.translate('fractions.split_overlay.no_bag_weight')}
          </p>
        )}

        {renderPreview()}

        {errors.filter(Boolean).map((error) => (
          <p key={error} className='text-red'>
            {error}
          </p>
        ))}
      </Body>
    </Overlay>
  );
};

export default observer(SplitFractionOverlay);
